<template>
  <div>
    <div class="flex justify-between py-2 mb-6 border-b border-gray-200 sm:flex-nowrap h-11">
      <h3 class="font-medium leading-5 text-gray-900 text-md">
        {{ transChoice('app.file', 2).titleCase() }}
      </h3>

      <div class="flex-shrink-0">
        <a
          v-if="canUploadFiles"
          class="tw-button-blue medium"
          @click="uploadFile()"
        >
          Upload
        </a>
      </div>
    </div>

    <div
      v-if="loading"
      class="centered loader is-medium"
    ></div>

    <div
      v-else
      class="content-holder"
    >
      <div v-if="! isEmpty">
        <dt class="text-sm font-medium text-gray-900 hide">Files</dt>
        <dd
          class="mt-2 text-sm text-gray-900 sm:col-span-2 sm:mt-0"
        >
          <ul role="list" class="border border-gray-200 divide-y divide-gray-100 rounded-md">
            <li
              v-for="file in files"
              :key="file.id"
              class="flex-col p-2 text-sm"
            >
              <div class="flex items-center justify-between py-1 text-sm">
                <div class="flex items-center flex-1 w-0">
                    <div class="flex flex-1 min-w-0 gap-2">
                        <a
                            :href="file.url"
                            :title="file.name"
                            class="font-medium truncate text-xs text-blue-600 hover:text-blue-500 underline hover:nounderline"
                        >
                            {{ file.name }}
                        </a>
                        <span class="flex-shrink-0 text-gray-400 text-xxs">{{ file.size }}</span>
                    </div>
                </div>
                <div class="flex-shrink-0 ml-4">
                  <button
                    v-if="file.user_can_delete"
                    type="button"
                    class="rounded-full bg-gray-300 p-1 -mr-1 text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-red-600"
                    title="Delete"
                    @click="deleteFile(file)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div>
                <span
                  v-if="file.project_name"
                  class="italic text-gray-200 text-xxs"
                >
                  Added to {{ transChoice('app.project', 1).titleCase() }}: {{ file.project_name }}
                </span>
                <span class="italic text-gray-400 text-xxs">
                  {{ trans('app.uploaded').titleCase() }} {{ file.created_at }} by {{ file.user_name }}
                </span>
              </div>
            </li>
          </ul>
        </dd>
      </div>

      <div
        v-else
        class="p-3 rounded bg-blue-50"
      >
        <div class="flex-1 ml-3 md:flex md:justify-between">
          <p class="text-sm text-gray-800">{{ trans('app.file.none') }}</p>
        </div>
      </div>
    </div>

    <tw-modal ref="modal">
        <template v-slot:content>
            <div class="w-full px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
                <div class="w-full sm:flex sm:items-start">
                    <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                        <!-- Heroicon name: outline/exclamation -->
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <div class="w-full mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <h3 class="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                            Are you sure you want to delete this file?
                        </h3>
                    </div>
                </div>
            </div>
            <div class="flex justify-end px-4 py-3 space-x-2 sm:space-x-4 bg-gray-50 sm:px-6">
                <button class="tw-button-gray" @click="onDeleteModalHidden">
                    Cancel
                </button>
                <button class="tw-button-red" @click="submit">
                    Confirm
                </button>
            </div>
        </template>
    </tw-modal>
  </div>
</template>

<script>
export default {
    props: {
        typeId: {
            type: Number,
            required: true,
        },

        type: {
            type: String,
            required: true,
        },

        projectId: {
            type: Number,
            required: false,
            default: null,
        },

        canUploadFiles: {
            type: Boolean,
            default: false,
        },
    },

    data () {
        return {
            files: [],
            loading: false,
            fileForDelete: null,
        };
    },

    listeners: {
        'files-updated' (data) {
            if (data.type === this.type && data.type_id === this.typeId) {
                this.fetchList();
            }
        },
    },

    computed: {
        actionRoute () {
            return this.route(
                'files.index',
                {
                    type: this.type,
                    type_id: this.typeId,
                }
            );
        },

        deleteActionUrl () {
            if (_.isNull(this.fileForDelete)) {
                return '';
            }
            return this.route(
                'files.destroy',
                {
                    type: this.type,
                    type_id: this.typeId,
                    id: this.fileForDelete.id,
                }
            );
        },

        isEmpty () {
            return _.isEmpty(this.files);
        },
    },

    methods: {
        uploadFile () {
            OSPEvent.$emit('open-file-upload-modal', this.type, this.typeId, this.projectId);
        },

        deleteFile (file) {
            this.$refs.modal.show();
            this.fileForDelete = file;
        },

        onDeleteModalHidden () {
            this.fileForDelete = null;
            this.$refs.modal.hide();
        },

        onDeleteSuccess () {
            this.$refs.modal.hide();
            this.fetchList();
        },

        async fetchList () {
            try {
                this.loading = true;

                let {data} = await axios.get(this.actionRoute);
                this.files = data.data;
            } catch (error) {
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        async submit() {
            try {
                this.loading = true;
                await axios.delete(this.deleteActionUrl);
                this.onDeleteSuccess();
            } catch (errors) {
                this.$emit('error');
                console.error(errors);
            } finally {
                this.loading = false;
            }
        },
    },

    created () {
        this.fetchList();
    },
};
</script>

<style>
.panel-block .thumbnail {
  width: 3em;
}
</style>
